import React, { useState } from 'react';
import moment from 'moment';
import { useEffectOnce } from 'react-use';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../CommonComponents/OPL/Button/Button';
import { Modal } from '../../../CommonComponents/OPL/Modal/Modal';
import { ModalBody } from '../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalCloseButton } from '../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalFooter } from '../../../CommonComponents/OPL/Modal/ModalFooter';
import { ModalHeader } from '../../../CommonComponents/OPL/Modal/ModalHeader';
import { Sections } from './ReportPreview.meta';
import { Error } from './components/Error/Error';
import { Loading } from './components/Loading/Loading';
import { useReportPreviewMutation } from '../../api/useReportPreviewMutation';
import { Input } from '../../../CommonComponents/OPL/Input/Input';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { Loading as LoadingSpinner } from '../../../CommonComponents/OPL/Loading/Loading';
import { downloadFile } from '../../../CommonUtils/downloadFile';
import { Alert } from '../../../CommonComponents/OPL/Alert/Alert';
import { PdfViewer } from '../../../CommonComponents/OPL/PdfViewer/PdfViewer';

export const INITIAL_OPTIONS = {
  isDraft: true,
  saveFile: false,
  customFileName: null,
};

interface Props {
  sections: Sections;
  onClose: () => void;
}

export const ReportPreview = ({ sections, onClose }: Props) => {
  const { mutateAsync: requestPdf, isLoading, error, data } = useReportPreviewMutation();

  useEffectOnce(() => {
    requestPdf({ sections, ...INITIAL_OPTIONS });
  });

  if (isLoading) {
    return <Loading onClose={onClose} />;
  }

  if (error) {
    return <Error onClose={onClose} />;
  }

  if (!data) {
    return null;
  }

  const { fileName, fileUrl } = data;

  return (
    <ReportContent fileURL={fileUrl} fileName={fileName} onClose={onClose} sections={sections} />
  );
};

interface Options {
  isDraft: boolean;
  saveFile: boolean;
  customFileName: string;
}

interface ReportContentProps {
  fileURL: string;
  fileName: string;
  onClose: () => void;
  sections: Sections;
}

const ReportContent = ({ fileURL, fileName, onClose, sections }: ReportContentProps) => {
  const [options, setOptions] = useState<Options>({ ...INITIAL_OPTIONS, customFileName: fileName });

  const { mutateAsync: saveReport, error, isLoading } = useReportPreviewMutation();

  return (
    <Modal isOpen onRequestClose={onClose} aria={{ labelledby: 'Generate Report' }} size="xl">
      <ModalHeader as="h2" id="modal-header">
        Generate Report
      </ModalHeader>

      <ModalCloseButton onClick={onClose} />

      <ModalBody>
        {error && <Alert message={error.message} variant="error" />}

        <Box display="flex" flexDirection="column">
          <Text color="neutral.500" mb="16">
            Review the draft report, then choose to download or print using the options below.
          </Text>

          <Input
            label="Report Name"
            mb="16"
            value={options.customFileName}
            onChange={({ target }) => {
              setOptions(state => ({ ...state, customFileName: target.value }));
            }}
          />

          <Box position="relative" height="720px">
            <PdfViewer
              fileName={fileName}
              url={fileURL}
              displayOptions={{
                showAnnotationTools: false,
                showLeftHandPanel: false,
                defaultViewMode: 'FIT_WIDTH',
                dockPageControls: false,
                showDownloadPDF: false,
                showPrintPDF: false,
              }}
              style={{ width: '100%' }}
            />
            {isLoading && (
              <Box
                alignItems="center"
                bg="neutral.900"
                display="flex"
                flex={1}
                flexDirection="column"
                height="100%"
                justifyContent="center"
                position="absolute"
                width="100%"
                css={{ opacity: 0.9, top: 0 }}
              >
                <LoadingSpinner text="Processing PDF" />
              </Box>
            )}
          </Box>
        </Box>
      </ModalBody>

      <ModalFooter>
        <Box flex={1}>
          <Button variant="subtleInactive" onClick={onClose}>
            Cancel
          </Button>
        </Box>

        <Button
          ml="8"
          isLoading={isLoading}
          onClick={async () => {
            const timestamp = moment().format('YYYY-MM-DD_HH-mm-ss');
            const finalFileName = `${options.customFileName} ${timestamp}`;

            try {
              const response = await saveReport({
                sections,
                saveFile: false,
                isDraft: true,
                customFileName: finalFileName,
              });

              const { fileBlob } = response;
              downloadFile(finalFileName, fileBlob);
            } catch (err) {
              console.error('Error downloading the report:', err);
            }
          }}
        >
          Download Draft Report
        </Button>

        <Button
          ml="8"
          isLoading={isLoading}
          onClick={async () => {
            const timestamp = moment().format('YYYY-MM-DD_HH-mm-ss');
            const finalFileName = `${options.customFileName} ${timestamp}`;

            try {
              const response = await saveReport({
                sections,
                saveFile: true,
                isDraft: false,
                customFileName: finalFileName,
              });

              const { fileBlob } = response;
              downloadFile(finalFileName, fileBlob);
            } catch (err) {
              console.error('Error downloading the report:', err);
            }
          }}
        >
          Save & Download Final Report
        </Button>
      </ModalFooter>
    </Modal>
  );
};
